<template>
  <!-- Deliberately not a second bootstrap modal, and deliberately not
       teleported out either. A modal on top of a modal fights over the
       backdrop and the focus trap — bootstrap pulls focus back into the
       dialog it owns, so a field outside it cannot be typed into. A fixed
       layer *inside* the dialog covers the screen just the same and keeps
       focus where bootstrap expects it. -->
  <div
    ref="scrim"
    class="menu-pop-scrim"
    tabindex="-1"
    @click.self="close"
    @keydown.esc.stop="escape"
  >
    <div
      class="menu-pop"
      role="dialog"
      aria-modal="true"
      aria-label="Edit menu links"
    >
      <div class="menu-pop-head">
        <span class="menu-pop-icon">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path d="M4 8l16 0" />
            <path d="M4 16l16 0" />
          </svg>
        </span>
        <div class="menu-pop-titles">
          <h5>{{ menu?.name }}</h5>
          <span class="menu-pop-sub">
            {{ items.length }} {{ items.length === 1 ? "link" : "links" }}
          </span>
        </div>
        <button class="icon-btn" title="Close" @click="close">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path
              d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
            />
          </svg>
        </button>
      </div>

      <!-- A menu is not part of this page: it belongs to the shop, and every
           page that shows it changes with it. Said here, before anything is
           moved, rather than found out afterwards. -->
      <div class="menu-pop-note">
        This menu is shared by every page that uses it, and each change here is
        saved right away — the page's own Save button does not cover it.
      </div>

      <div class="menu-pop-body">
        <div class="menu-rows" v-if="items.length">
          <draggable
            :list="items"
            item-key="id"
            handle=".drag-handle"
            animation="200"
            @change="reorder"
          >
            <template #item="{ element }">
              <div class="menu-row" :class="{ busy: busyId === element.id }">
                <span class="drag-handle" title="Drag to reorder">
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <circle cx="9" cy="5" r="1.6" />
                    <circle cx="15" cy="5" r="1.6" />
                    <circle cx="9" cy="12" r="1.6" />
                    <circle cx="15" cy="12" r="1.6" />
                    <circle cx="9" cy="19" r="1.6" />
                    <circle cx="15" cy="19" r="1.6" />
                  </svg>
                </span>
                <input
                  class="menu-row-title"
                  :value="titleOf(element, lang)"
                  :placeholder="fallbackTitle(element) || 'Link text'"
                  @input="setTitle(element, lang, $event.target.value)"
                  @change="saveItem(element)"
                />
                <!-- Where the link goes, by name. "/category/31-" said the
                     same thing in a form nobody can check at a glance. -->
                <span class="menu-row-target">
                  <span
                    class="badge-soft"
                    :class="targetOf(element).badge"
                    v-if="targetOf(element).kind"
                  >
                    {{ targetOf(element).kind }}
                  </span>
                  <span class="menu-row-where" :title="targetOf(element).label">
                    {{ targetOf(element).label }}
                  </span>
                </span>
                <button
                  class="icon-btn icon-btn-accent"
                  :class="{ open: openLangs === element.id }"
                  title="Text in other languages"
                  @click="toggleLangs(element)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="currentColor"
                  >
                    <path
                      d="M18.5 10l4.4 11h-2.155l-1.201-3h-4.09l-1.199 3h-2.154L16.5 10h2zM10 2v2h6v2h-1.968a18.222 18.222 0 0 1-3.62 6.301 14.864 14.864 0 0 0 2.336 1.707l-.751 1.878A17.015 17.015 0 0 1 9 13.725a16.676 16.676 0 0 1-6.201 3.548l-.536-1.929a14.7 14.7 0 0 0 5.327-3.042A18.078 18.078 0 0 1 4.767 8h2.24A16.032 16.032 0 0 0 9 10.877a16.165 16.165 0 0 0 2.91-4.876L2 6V4h6V2h2zm7.5 10.885L16.253 16h2.492L17.5 12.885z"
                    />
                  </svg>
                </button>
                <button
                  class="icon-btn icon-btn-danger"
                  title="Remove this link"
                  @click="askRemove = element"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="currentColor"
                  >
                    <path
                      d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                    />
                  </svg>
                </button>
                <!-- Every language at once, on the row it belongs to: the
                     other languages are the most common reason a link reads
                     wrong on the live site, and they were a separate dialog
                     away. -->
                <div class="menu-row-langs" v-if="openLangs === element.id">
                  <label v-for="l in languages" :key="l.code">
                    <span>{{ l.name }}</span>
                    <input
                      :value="titleOf(element, l.code)"
                      @input="setTitle(element, l.code, $event.target.value)"
                      @change="saveItem(element)"
                    />
                  </label>
                </div>
              </div>
            </template>
          </draggable>
        </div>
        <div class="menu-empty" v-else>
          This menu has no links yet. Add the first one below.
        </div>

        <div class="menu-add">
          <span class="field-label">Add a link</span>
          <div class="menu-add-row">
            <v-select
              class="menu-add-target"
              append-to-body
              :calculate-position="dropdownBelowField"
              v-model="newTarget"
              :options="targetOptions"
              label="name"
              :filter="filterTargets"
              taggable
              :create-option="(url) => ({ name: url, target_url: url })"
              placeholder="Page, category or web address…"
            >
              <template v-slot:option="option">
                <span class="menu-opt-name">{{ option.name }}</span>
                <small class="menu-opt-meta">{{ option.kind }}</small>
              </template>
              <template v-slot:no-options>
                Type a web address to link anywhere.
              </template>
            </v-select>
            <input
              class="menu-add-title"
              v-model="newTitle"
              placeholder="Link text"
            />
            <button
              class="btn btn-primary"
              :disabled="!canAdd || adding"
              @click="addItem"
            >
              {{ adding ? "Adding…" : "Add" }}
            </button>
          </div>
        </div>

        <div class="menu-pop-error" v-if="error">{{ error }}</div>
      </div>

      <div class="menu-pop-foot">
        <a href="/menu" target="_blank" rel="noopener" class="menu-pop-link">
          Open the full menu editor
        </a>
        <button class="btn btn-primary" @click="close">Done</button>
      </div>

      <!-- Removing a link is the one step here nothing undoes, and it takes
           effect on the live site as soon as it is clicked. -->
      <div class="menu-confirm" v-if="askRemove">
        <div class="menu-confirm-box">
          <h5>Remove this link?</h5>
          <p>
            “{{ fallbackTitle(askRemove) || "This link" }}” disappears from
            every page that shows this menu, right away.
          </p>
          <div class="menu-confirm-actions">
            <button class="text-btn" @click="askRemove = null">Cancel</button>
            <button class="btn btn-danger" @click="removeItem(askRemove)">
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import http from "../../modules/http";
import { dropdownBelowField } from "../../modules/select-position";

// Pages and categories are the same for every menu and every block, so they
// are fetched once per language and kept for the session.
const targetCache = {};

function flattenCategories(categories, parentName = "") {
  const flat = [];
  for (const item of categories ?? []) {
    const name = parentName ? `${parentName} / ${item.name}` : item.name;
    flat.push({
      name,
      kind: "Category",
      target_category_id: item.id,
      slug: item.slug,
    });
    // The category's bundle listing (/category/31-slug/bundles) has no id of
    // its own: it is saved as an address, right after its category — and only
    // for a category that has bundles (the shop answers 404 otherwise).
    if (item.has_bundles) {
      flat.push({
        name: `${name} / Bundles`,
        kind: "Bundles",
        target_url: `/category/${item.id}-${item.slug ?? ""}/bundles`,
      });
    }
    if (item.categories !== undefined) {
      flat.push(...flattenCategories(item.categories, name));
    }
  }
  return flat;
}

async function loadTargets(language) {
  if (!targetCache[language]) {
    targetCache[language] = (async () => {
      const [pages, categories, bundleGroups] = await Promise.all([
        http.fetch("/page"),
        http.fetch("/products/categories/products/" + language + "?bundles=1"),
        http.fetch("/products/bundle-groups/" + language).catch(() => null),
      ]);
      return [
        ...flattenCategories(categories?.data ?? []),
        // A bundle group's listing (/bundles/2-hoodie-szettek), saved as an address.
        ...(bundleGroups?.data ?? []).map((group) => ({
          name: `Bundle list / ${group.name}`,
          kind: "Bundles",
          target_url: `/bundles/${group.id}-${group.slug}`,
        })),
        ...(pages?.data ?? []).map((page) => ({
          name: page.name,
          kind: "Page",
          target_page_id: page.id,
          slug: page.slug,
        })),
      ];
    })().catch(() => {
      delete targetCache[language];
      return [];
    });
  }
  return targetCache[language];
}

export default {
  name: "MenuLinksEditor",
  components: { draggable },
  props: {
    // The menu record as /menu returns it. Its menu_items array is updated in
    // place, so whatever else holds this menu (the preview, the picker) stays
    // in step without a reload.
    menu: { type: Object, default: null },
    languages: { type: Array, default: () => [] },
    language: { type: String, default: null },
  },
  emits: ["close", "changed"],
  data() {
    return {
      items: [],
      lang: this.language,
      openLangs: null,
      askRemove: null,
      busyId: null,
      adding: false,
      error: "",
      targetOptions: [],
      newTarget: null,
      newTitle: "",
    };
  },
  computed: {
    canAdd() {
      return Boolean(this.newTarget) && this.newTitle.trim().length > 0;
    },
  },
  watch: {
    // Picking a page or category names the link for you; a name already typed
    // is left alone.
    newTarget(target) {
      if (target && !this.newTitle.trim()) {
        this.newTitle = target.name ?? "";
      }
    },
  },
  mounted() {
    // Focus lands inside the popup: Escape then closes this and not the block
    // dialog underneath, and bootstrap's focus trap — which pulls focus back
    // into the dialog it owns — has nothing to complain about.
    this.$refs.scrim?.focus();
    this.items = [...(this.menu?.menu_items ?? [])];
    if (!this.lang) {
      this.lang = this.languages?.[0]?.code;
    }
    loadTargets(this.lang ?? "en").then((options) => {
      this.targetOptions = options;
    });
  },
  methods: {
    dropdownBelowField,
    close() {
      this.$emit("close");
    },
    // Escape closes the popup — unless a dropdown is open inside it, which is
    // what Escape is for at that moment. (It never reaches the block dialog
    // underneath: the handler on the layer stops it there.)
    escape(event) {
      if (event.target?.closest?.(".vs--open")) {
        return;
      }
      this.close();
    },
    titleOf(item, code) {
      return item?.translations?.[code] ?? "";
    },
    // What to show when this language has no text yet: the first one that has.
    fallbackTitle(item) {
      const translations = item?.translations ?? {};
      return (
        translations[this.lang] ??
        Object.values(translations).find((value) => value) ??
        ""
      );
    },
    setTitle(item, code, value) {
      if (!item.translations) {
        item.translations = {};
      }
      item.translations[code] = value;
    },
    // A link points at a category, a page, or an address typed in by hand.
    // The first two are shown by name — the id behind them is the site's
    // business. Until the lists arrive, the kind alone is still true.
    targetOf(item) {
      if (item?.target_category_id) {
        const match = this.targetOptions.find(
          (o) =>
            String(o.target_category_id) === String(item.target_category_id)
        );
        return {
          kind: "Category",
          badge: "badge-blue",
          label: match?.name ?? "",
        };
      }
      if (item?.target_page_id) {
        const match = this.targetOptions.find(
          (o) => String(o.target_page_id) === String(item.target_page_id)
        );
        return {
          kind: "Page",
          badge: "badge-yellow",
          label: match?.name ?? "",
        };
      }
      if (item?.target_url) {
        const match = this.targetOptions.find(
          (o) => o.kind === "Bundles" && o.target_url === item.target_url
        );
        if (match) {
          return { kind: "Bundles", badge: "badge-blue", label: match.name };
        }
        return { kind: "", badge: "", label: item.target_url };
      }
      return { kind: "", badge: "", label: "" };
    },
    toggleLangs(item) {
      this.openLangs = this.openLangs === item.id ? null : item.id;
    },
    filterTargets(options, search) {
      const q = search.toLowerCase();
      return options.filter((o) => o.name?.toLowerCase().includes(q));
    },
    // Everything below writes to the shop straight away — there is no draft
    // state for a menu, and pretending otherwise would lose the change when
    // the dialog closes.
    async saveItem(item) {
      this.busyId = item.id;
      this.error = "";
      try {
        await http.fetch(
          `/menus/${this.menu.id}/menuitem/${item.id}`,
          item,
          false,
          "PUT"
        );
        this.publish();
      } catch (e) {
        this.error = "The link text could not be saved.";
      }
      this.busyId = null;
    },
    async reorder() {
      this.error = "";
      try {
        await http.fetch(
          `/menus/${this.menu.id}/menuitem/order`,
          {
            data: this.items.map((item, index) => ({
              id: item.id,
              position: index,
            })),
          },
          false,
          "PUT"
        );
        this.publish();
      } catch (e) {
        this.error = "The new order could not be saved.";
      }
    },
    async addItem() {
      if (!this.canAdd) {
        return;
      }
      this.adding = true;
      this.error = "";
      const translations = {};
      translations[this.lang] = this.newTitle.trim();
      const add = { order: this.items.length, translations };
      if (this.newTarget.target_category_id !== undefined) {
        add.target_category_id = this.newTarget.target_category_id;
      } else if (this.newTarget.target_page_id !== undefined) {
        add.target_page_id = this.newTarget.target_page_id;
      } else {
        add.target_url = this.newTarget.target_url ?? this.newTarget.name;
      }
      try {
        await http.fetch(`/menus/${this.menu.id}/menuitem`, add);
        // The new link needs the id the server gave it — without it the next
        // rename or removal would be aimed at nothing. Re-reading the menu is
        // one request and leaves no room for a mismatch.
        await this.reload();
        this.newTarget = null;
        this.newTitle = "";
      } catch (e) {
        this.error = "The link could not be added.";
      }
      this.adding = false;
    },
    async removeItem(item) {
      this.askRemove = null;
      this.busyId = item.id;
      this.error = "";
      try {
        await http.fetch(
          `/menus/${this.menu.id}/menuitem/${item.id}`,
          {},
          false,
          "DELETE"
        );
        this.items = this.items.filter((one) => one.id !== item.id);
        this.publish();
      } catch (e) {
        this.error = "The link could not be removed.";
      }
      this.busyId = null;
    },
    async reload() {
      const res = await http.fetch("/menu");
      const fresh = (res?.data ?? []).find(
        (one) => String(one.id) === String(this.menu.id)
      );
      this.items = [...(fresh?.menu_items ?? [])];
      this.publish();
    },
    // The editor keeps its own copy of the menus (the preview draws from it),
    // so every change is handed up rather than written into the prop.
    publish() {
      this.$emit("changed", [...this.items]);
    },
  },
};
</script>

<style scoped>
.menu-pop-scrim {
  outline: none;
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.menu-pop {
  width: min(720px, 100%);
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(18, 18, 18, 0.3);
  overflow: hidden;
  position: relative;
}
.menu-pop-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
}
.menu-pop-icon {
  display: inline-grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(124, 58, 237, 0.12);
  color: #5b21b6;
  flex: none;
}
.menu-pop-titles {
  flex: 1;
  min-width: 0;
}
.menu-pop-head .lang-pick {
  margin-right: 6px;
}
.menu-pop-titles h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.menu-pop-sub {
  font-size: 11px;
  font-weight: 600;
  color: #768692;
}
.menu-pop-note {
  padding: 9px 16px;
  background: #fef9e7;
  color: #8d7803;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.5;
}
.menu-pop-body {
  padding: 12px 16px 16px;
  overflow-y: auto;
}
.menu-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 7px 6px;
  border-radius: 9px;
  border: 1px solid transparent;
}
.menu-row:hover {
  background: #f8fafc;
  border-color: #e2e8f0;
}
.menu-row.busy {
  opacity: 0.55;
}
.drag-handle {
  flex: none;
  cursor: grab;
  color: #c0bdbd;
  display: inline-grid;
  place-items: center;
}
.drag-handle svg {
  fill: currentColor;
}
.menu-row-title {
  flex: 1 1 180px;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 7px;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 700;
  background: transparent;
  color: #121212;
}
.menu-row-title:hover {
  border-color: #e2e8f0;
}
.menu-row-title:focus {
  border-color: #7c3aed;
  background: #fff;
  outline: none;
}
.menu-row-target {
  flex: 0 1 230px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #768692;
}
.menu-row-where {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.icon-btn.open {
  background: rgba(124, 58, 237, 0.12);
  color: #5b21b6;
}
.menu-row-langs {
  flex: 1 0 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 6px;
  padding: 8px 0 4px 28px;
}
.menu-row-langs label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  color: #768692;
}
.menu-row-langs span {
  flex: none;
  width: 68px;
}
.menu-row-langs input {
  flex: 1;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 5px 8px;
  font-size: 12.5px;
  font-weight: 600;
}
.menu-empty {
  padding: 18px 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #768692;
}
.menu-add {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}
.menu-add-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.menu-add-target {
  flex: 1 1 260px;
  min-width: 0;
}
.menu-add-title {
  flex: 0 1 190px;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 13px;
  font-weight: 600;
}
.menu-pop-error {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 700;
  color: #d22f0b;
}
.menu-pop-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
}
.menu-pop-link {
  font-size: 12px;
  font-weight: 700;
  color: #7c3aed;
  text-decoration: none;
}
.menu-pop-link:hover {
  text-decoration: underline;
}
.menu-confirm {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.82);
  display: grid;
  place-items: center;
  padding: 20px;
}
.menu-confirm-box {
  width: min(360px, 100%);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.16);
  padding: 16px;
}
.menu-confirm-box h5 {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
}
.menu-confirm-box p {
  margin: 0 0 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: #505354;
  line-height: 1.5;
}
.menu-confirm-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
</style>
